import { cn } from '../../lib/utils';
import React from 'react'
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu'

export default function DataTablePagination({
  page,
  totalPages,
  setPage,
  limit,
  setLimit,
  isDark = false,
}) {
  return (
    <div className={cn(
      "flex flex-col sm:flex-row gap-[12px] sm:gap-0 justify-between items-center transition-colors duration-200",
      isDark ? 'text-[#f5f5f5]' : 'text-[#000000]'
    )}>
      {/* Numbered pagination */}
      <nav className={cn("flex flex-wrap gap-[8px] font-medium text-[12px]")}>
        {/* Previous */}
        <button
          onClick={() => setPage(p => Math.max(p - 1, 1))}
          disabled={page === 1}
          className={cn(
            "px-3 py-1 flex items-center justify-center rounded border-solid border-[1px] disabled:opacity-50 transition-colors duration-200",
            isDark
              ? 'bg-[#1a1a1a] border-[#292929] text-[#f5f5f5] hover:bg-[#232323]'
              : 'bg-white border-[#d9d9d9] text-[#000000] hover:bg-[#f5f5f5]'
          )}
        >
          <ChevronLeft className={cn("w-4 h-4")} />
        </button>

        {/* Page buttons */}
        {[...Array(totalPages)].map((_, i) => {
          const pageNum = i + 1;
          const showOnMobile = pageNum === page;
          const showOnDesktop = pageNum === 1 || pageNum === totalPages || (pageNum >= page - 2 && pageNum <= page + 2);

          if (showOnDesktop) {
            return (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={cn(
                  "px-3 py-1 rounded border-solid border-[1px] transition-colors duration-200",
                  isDark
                    ? pageNum === page
                      ? 'bg-[#cd6d2c] text-[#161515] border-[#cd6d2c]'
                      : 'bg-[#1a1a1a] text-[#f5f5f5] border-[#292929] hover:bg-[#232323]'
                    : pageNum === page
                      ? 'bg-[#072837] text-white border-[#072837]'
                      : 'bg-white text-[#000000] border-[#d9d9d9] hover:bg-[#f5f5f5]',
                  !showOnMobile ? 'hidden sm:inline-block' : ''
                )}
              >
                {pageNum}
              </button>
            );
          } else if (pageNum === page - 4 || pageNum === page + 4) {
            return <span key={`ellipsis-${pageNum}`} className={cn("hidden sm:inline")}>...</span>;
          }
          return null;
        })}

        {/* Next */}
        <button
          onClick={() => setPage(p => Math.min(p + 1, totalPages))}
          disabled={page === totalPages || totalPages === 0}
          className={cn(
            "px-3 py-1 flex items-center justify-center rounded border-solid border-[1px] disabled:opacity-50 transition-colors duration-200",
            isDark
              ? 'bg-[#1a1a1a] border-[#292929] text-[#f5f5f5] hover:bg-[#232323]'
              : 'bg-white border-[#d9d9d9] text-[#000000] hover:bg-[#f5f5f5]'
          )}
        >
          <ChevronRight className={cn("w-4 h-4")} />
        </button>
      </nav>
      {/* Rows per page selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded text-[14px] transition-colors duration-200",
              isDark
                ? 'bg-[#161515] text-[#f2f2f2] hover:bg-[#222222] border-0'
                : 'bg-white border border-solid border-[#d9d9d9] text-[#072837] hover:bg-[#f5f5f5]'
            )}
          >
            {limit} rows
            <ChevronDown className={cn("w-4 h-4")} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={cn(isDark 
          ? 'bg-[#161515] border-0' 
          : 'bg-white border border-solid border-[#d9d9d9]', "rounded-[12px] p-0 overflow-hidden min-w-[120px]")}>
          {[10, 20, 50, 100].map((size) => (
            <DropdownMenuItem
              key={size}
              className={cn(isDark
                ? limit === size
                  ? 'bg-[#cd6d2c] text-[#161515]'
                  : 'bg-[#161515] text-[#f2f2f2] hover:bg-[#222222] hover:text-[#cd6d2c]'
                : limit === size
                  ? 'bg-[#072837] text-white hover:!bg-[#072837]'
                  : 'bg-white text-[#072837] hover:!bg-[#f5f5f5]', "text-[13px] font-medium px-[16px] py-[10px] cursor-pointer transition-colors duration-200")}
              onClick={() => {
                setLimit?.(size);
                setPage?.(1);
              }}
            >
              {size} rows
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
